/**
 * Helpers for LiteJsonEditor form-control sync.
 * Kept pure so invalid→valid recovery can be unit-tested without Angular TestBed.
 */

/** Collapse editor innerText the same way the component does before JSON.parse. */
export function normalizeJsonEditorText(innerText: string): string {
  return innerText.split(/[\xa0\n]+/).join('');
}

/**
 * Normalize a form-written value for display.
 *
 * Editable editors turn null/undefined into {} so the user can start adding keys.
 * Read-only editors must not fabricate an empty object for genuinely absent data,
 * so they keep it empty ('') — matching how the editor rendered before CAR-1899.
 * Non-null values (objects, arrays, primitives) always render as-is.
 */
export function normalizeWrittenValue(rawValue: unknown, readOnly: boolean): unknown {
  if (rawValue != null) {
    return rawValue;
  }
  return readOnly ? '' : {};
}

/**
 * Whether a FormControl value is acceptable JSON for the extras editor.
 *
 * @param lastEmittedWasInvalid - when true, a non-parseable string is treated as an
 *   invalid draft (user is mid-edit). When false, a plain string is treated as a
 *   successfully parsed JSON string primitive (e.g. `"hello"` → `hello`).
 */
export function isJsonControlValueValid(
  value: unknown,
  lastEmittedWasInvalid = false
): boolean {
  if (value == null || value === '') {
    return true;
  }
  if (typeof value === 'object') {
    try {
      JSON.stringify(value);
      return true;
    } catch {
      return false;
    }
  }
  if (typeof value === 'string') {
    if (value.length === 0) {
      return true;
    }
    try {
      JSON.parse(value);
      return true;
    } catch {
      // Invalid draft string vs parsed JSON string primitive — see flag.
      return !lastEmittedWasInvalid;
    }
  }
  // number | boolean (parsed JSON primitives)
  return true;
}

/**
 * After a successful parse, decide whether to push the object into the FormControl.
 *
 * Always emit when the previous emit was an invalid string — otherwise fixing the
 * text back to the same object hides the error popup while the control stays
 * invalid and Save silently no-ops.
 */
export function shouldPropagateParsedJson(
  parsed: unknown,
  modelValue: unknown,
  lastEmittedWasInvalid: boolean
): boolean {
  if (lastEmittedWasInvalid) {
    return true;
  }
  return JSON.stringify(parsed) !== JSON.stringify(modelValue);
}
